import type { ReactNode } from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Tag,
  List,
  Empty,
  Button,
  Space,
  Typography,
  Progress,
} from 'antd';
import {
  FileTextOutlined,
  TeamOutlined,
  CrownOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';
import { useEvalRoleLabels } from '../contexts/EvalRoleLabelsContext';
import { taskStatusColor, taskStatusLabel } from '../utils/taskStatusDisplay';

export function DashboardPage() {
  const { getEvalRoleLabel } = useEvalRoleLabels();
  const profile = useAuthStore((s) => s.profile);
  const navigate = useNavigate();
  const isLeader = profile?.roles?.includes('LEADER') ?? false;
  const isNominator = profile?.roles?.includes('NOMINATOR') ?? false;

  const { data: currentBatch } = useQuery({
    queryKey: ['batch', 'current'],
    queryFn: () => api.get('/batches/current'),
  });

  const { data: myTasks = [] } = useQuery({
    queryKey: ['my-tasks'],
    queryFn: () => api.get('/my/eval-tasks'),
  });

  const { data: compTasks = [] } = useQuery({
    queryKey: ['my-competency-tasks'],
    queryFn: () => api.get('/leader/competency-tasks'),
    enabled: isLeader,
  });

  const { data: hpTasks = [] } = useQuery({
    queryKey: ['my-hp-tasks'],
    queryFn: () => api.get('/leader/hp-tasks'),
    enabled: isLeader,
  });

  const evalList = myTasks as any[];
  const compList = compTasks as any[];
  const hpList = hpTasks as any[];

  const evalPending = evalList.filter((t) => t.status !== 'SUBMITTED').length;
  const evalDone = evalList.filter((t) => t.status === 'SUBMITTED').length;
  const evalPct = evalList.length ? Math.round((evalDone / evalList.length) * 100) : 0;

  const compPending = compList.filter((t) => t.status !== 'SUBMITTED').length;
  const compDone = compList.filter((t) => t.status === 'SUBMITTED').length;
  const compPct = compList.length ? Math.round((compDone / compList.length) * 100) : 0;

  const hpPending = hpList.filter((t) => !t.assessment || t.assessment.status !== 'SUBMITTED').length;
  const hpDone = hpList.length - hpPending;
  const hpPct = hpList.length ? Math.round((hpDone / hpList.length) * 100) : 0;

  const batchName = (currentBatch as any)?.name || '暂无进行中批次';

  const sectionCard = (
    title: string,
    icon: ReactNode,
    subtitle: string,
    total: number,
    pending: number,
    pct: number,
    color: string,
    onEnter: () => void,
    extra?: ReactNode,
  ) => (
    <Card
      style={{ borderRadius: 12, height: '100%', borderTop: `3px solid ${color}` }}
      styles={{ body: { padding: '16px 18px' } }}
    >
      <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space direction="vertical" size={4} style={{ flex: 1 }}>
          <Space>
            <span style={{ color, fontSize: 20 }}>{icon}</span>
            <Typography.Text strong style={{ fontSize: 16 }}>
              {title}
            </Typography.Text>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {subtitle}
          </Typography.Text>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Statistic title="待完成" value={pending} valueStyle={{ color: '#cf1322', fontSize: 22 }} />
            </Col>
            <Col span={12}>
              <Statistic title="合计" value={total} valueStyle={{ fontSize: 22 }} />
            </Col>
          </Row>
          {total > 0 && (
            <Progress percent={pct} size="small" strokeColor={color} style={{ marginTop: 8 }} />
          )}
        </Space>
        <Button type="primary" onClick={onEnter} disabled={total === 0}>
          {total === 0 ? '暂无' : '去处理'}
        </Button>
      </Space>
      {extra}
    </Card>
  );

  return (
    <div
      style={{
        margin: -24,
        padding: 24,
        minHeight: 'calc(100vh - 64px)',
        background: 'linear-gradient(180deg, #f5f0ff 0%, #faf8ff 35%, #ffffff 100%)',
      }}
    >
      <div className="tg-page-title" style={{ marginBottom: 8 }}>
        欢迎，{profile?.name}
        <Tag color="blue" style={{ marginLeft: 12 }}>
          {profile?.roles.join(' / ')}
        </Tag>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 20 }}>
        当前批次：<Typography.Text strong>{batchName}</Typography.Text>
        ｜下面按类型列出与您相关的评价待办，请优先处理标红「待完成」。
      </Typography.Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          {sectionCard(
            '素质项评价',
            <FileTextOutlined />,
            '多角色对同事的素质维度打分（评价关系由 HR 配置）',
            evalList.length,
            evalPending,
            evalPct,
            '#722ed1',
            () => {
              const next = evalList.find((t) => t.status !== 'SUBMITTED') || evalList[0];
              if (next) navigate(`/eval/${next.id}`);
            },
            evalList.length > 0 ? (
              <List
                size="small"
                style={{ marginTop: 12 }}
                dataSource={evalList.slice(0, 4)}
                locale={{ emptyText: '' }}
                renderItem={(task) => (
                  <List.Item style={{ padding: '6px 0' }}>
                    <Space wrap>
                      <Typography.Text>{task.evaluatee?.name}</Typography.Text>
                      <Tag>{getEvalRoleLabel((task as any).template?.pool, task.roleInEval)}</Tag>
                      <Tag color={taskStatusColor(task.status)}>{taskStatusLabel(task.status)}</Tag>
                      <Button type="link" size="small" onClick={() => navigate(`/eval/${task.id}`)}>
                        {task.status === 'SUBMITTED' ? '查看' : '打分'}
                      </Button>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无素质项任务" style={{ margin: '12px 0' }} />
            ),
          )}
        </Col>

        <Col xs={24} lg={12}>
          {isLeader
            ? sectionCard(
                '岗位胜任力评价',
                <TeamOutlined />,
                '直属领导对下属岗位胜任力维度打分',
                compList.length,
                compPending,
                compPct,
                '#1677ff',
                () => navigate('/leader/competency'),
                compList.length > 0 ? (
                  <List
                    size="small"
                    style={{ marginTop: 12 }}
                    dataSource={compList.slice(0, 4)}
                    renderItem={(row) => (
                      <List.Item style={{ padding: '6px 0' }}>
                        <Space>
                          <Typography.Text>{row.evaluatee?.name}</Typography.Text>
                          <Tag color={taskStatusColor(row.status)}>{taskStatusLabel(row.status)}</Tag>
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : null,
              )
            : (
                <Card style={{ borderRadius: 12, height: '100%', opacity: 0.85 }}>
                  <Space direction="vertical">
                    <Typography.Text type="secondary">
                      <TeamOutlined /> 岗位胜任力评价
                    </Typography.Text>
                    <Typography.Text type="secondary">仅直属领导可见；您当前无此待办。</Typography.Text>
                  </Space>
                </Card>
              )}
        </Col>

        <Col xs={24} lg={12}>
          {isLeader
            ? sectionCard(
                '下属高潜识别',
                <CrownOutlined />,
                '对直属下属做高潜识别：四维、STAR 与培养方向',
                hpList.length,
                hpPending,
                hpPct,
                '#fa8c16',
                () => navigate('/leader/hp'),
                hpList.length > 0 ? (
                  <List
                    size="small"
                    style={{ marginTop: 12 }}
                    dataSource={hpList.slice(0, 4)}
                    renderItem={(row) => (
                      <List.Item style={{ padding: '6px 0' }}>
                        <Space>
                          <Typography.Text>{row.evaluatee?.name}</Typography.Text>
                          <Tag color={taskStatusColor(row.assessment?.status || 'PENDING')}>
                            {taskStatusLabel(row.assessment?.status || 'PENDING')}
                          </Tag>
                        </Space>
                      </List.Item>
                    )}
                  />
                ) : null,
              )
            : (
                <Card style={{ borderRadius: 12, height: '100%', opacity: 0.85 }}>
                  <Space direction="vertical">
                    <Typography.Text type="secondary">
                      <CrownOutlined /> 下属高潜识别
                    </Typography.Text>
                    <Typography.Text type="secondary">仅直属领导可见；您当前无此待办。</Typography.Text>
                  </Space>
                </Card>
              )}
        </Col>

        <Col xs={24} lg={12}>
          {isNominator ? (
            <Card
              style={{ borderRadius: 12, height: '100%', borderTop: '3px solid #52c41a' }}
              styles={{ body: { padding: '16px 18px' } }}
            >
              <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space direction="vertical" size={4}>
                  <Space>
                    <span style={{ color: '#52c41a', fontSize: 20 }}>
                      <ProfileOutlined />
                    </span>
                    <Typography.Text strong style={{ fontSize: 16 }}>
                      晋升提名
                    </Typography.Text>
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    提名领导填写被提名人、亮点与晋升建议；请在表单内查看本人配额。
                  </Typography.Text>
                </Space>
                <Button type="primary" onClick={() => navigate('/nominator')}>
                  进入提名
                </Button>
              </Space>
            </Card>
          ) : (
            <Card style={{ borderRadius: 12, height: '100%', opacity: 0.85 }}>
              <Space direction="vertical">
                <Typography.Text type="secondary">
                  <ProfileOutlined /> 晋升提名
                </Typography.Text>
                <Typography.Text type="secondary">仅提名领导可见；您当前无此入口。</Typography.Text>
              </Space>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
